/**
 * Pure TEA update for comment-thread UI state (open/active/hovered/visible).
 *
 * Shell: `useCommentThreads.ts` (comment surface, doc mutations, timers).
 */
import { assertNever } from "../lib/assertNever";
import { isJust, isNothing, just, nothing, type Maybe } from "../lib/maybe";
import type { UpdateFn } from "../lib/tea";

export type CommentThreadsModel = {
  readonly activeId: Maybe<string>;
  /** At most one open margin card. */
  readonly openId: Maybe<string>;
  readonly hoveredCommentId: Maybe<string>;
  readonly commentsVisible: boolean;
};

export function initialCommentThreadsModel(
  activeId: Maybe<string> = nothing,
): CommentThreadsModel {
  return {
    activeId,
    openId: nothing,
    hoveredCommentId: nothing,
    commentsVisible: false,
  };
}

export type CommentThreadsMsg =
  | { readonly type: "ActiveIdSynced"; readonly id: Maybe<string> }
  | { readonly type: "SetActiveId"; readonly id: Maybe<string> }
  | {
      readonly type: "SyncOpenThreads";
      readonly anchorIds: readonly string[];
    }
  | {
      readonly type: "OpenThread";
      readonly id: string;
      readonly mobileShell: boolean;
    }
  | { readonly type: "CloseThread"; readonly id: string }
  | {
      readonly type: "SelectComment";
      readonly id: string;
      readonly focusLine: boolean;
      readonly mobileShell: boolean;
    }
  | {
      readonly type: "ReplyOpened";
      readonly threadId: string;
      readonly mobileShell: boolean;
    }
  | { readonly type: "ClearActiveIf"; readonly id: string }
  | {
      readonly type: "ThreadRemoved";
      readonly id: string;
    }
  | {
      readonly type: "CommentTextClick";
      readonly id: string;
      readonly mobileShell: boolean;
    }
  | { readonly type: "SetHovered"; readonly id: Maybe<string> }
  | { readonly type: "SetCommentsVisible"; readonly visible: boolean }
  | { readonly type: "ScheduleReveal"; readonly delayMs: number }
  | { readonly type: "RevealComments" };

export type CommentThreadsCmd =
  | { readonly kind: "bridgeActiveCommentId"; readonly id: Maybe<string> }
  | { readonly kind: "syncOpenCommentFacet"; readonly id: Maybe<string> }
  | { readonly kind: "scheduleLayout" }
  | { readonly kind: "openMobileComments" }
  | { readonly kind: "emitSelectComment"; readonly id: string }
  | { readonly kind: "scrollToComment"; readonly id: string }
  | { readonly kind: "flashComment"; readonly id: string }
  | { readonly kind: "scheduleReveal"; readonly delayMs: number };

function sameId(a: Maybe<string>, b: Maybe<string>): boolean {
  if (a.tag !== b.tag) return false;
  if (a.tag === "nothing") return true;
  return b.tag === "just" && a.value === b.value;
}

function openThreadCmds(
  id: string,
  mobileShell: boolean,
  alreadyOpen: boolean,
): CommentThreadsCmd[] {
  if (alreadyOpen) return [];
  const cmds: CommentThreadsCmd[] = [
    { kind: "syncOpenCommentFacet", id: just(id) },
    { kind: "scheduleLayout" },
  ];
  if (mobileShell) {
    cmds.push({ kind: "openMobileComments" });
  }
  return cmds;
}

export function updateCommentThreads(
  model: CommentThreadsModel,
  msg: CommentThreadsMsg,
): readonly [CommentThreadsModel, readonly CommentThreadsCmd[]] {
  switch (msg.type) {
    case "ActiveIdSynced": {
      if (sameId(model.activeId, msg.id)) return [model, []];
      return [{ ...model, activeId: msg.id }, []];
    }
    case "SetActiveId": {
      if (sameId(model.activeId, msg.id)) return [model, []];
      return [
        { ...model, activeId: msg.id },
        [{ kind: "bridgeActiveCommentId", id: msg.id }],
      ];
    }
    case "SyncOpenThreads": {
      const ids = new Set(msg.anchorIds);
      const stillOpen =
        isJust(model.openId) && ids.has(model.openId.value)
          ? model.openId
          : nothing;
      if (sameId(stillOpen, model.openId)) {
        return [model, []];
      }
      return [
        { ...model, openId: stillOpen },
        [{ kind: "syncOpenCommentFacet", id: stillOpen }],
      ];
    }
    case "OpenThread": {
      const alreadyOpen = isJust(model.openId) && model.openId.value === msg.id;
      if (alreadyOpen) return [model, []];
      return [
        { ...model, openId: just(msg.id) },
        openThreadCmds(msg.id, msg.mobileShell, false),
      ];
    }
    case "CloseThread": {
      if (isNothing(model.openId) || model.openId.value !== msg.id) {
        return [model, []];
      }
      const clearActive =
        isJust(model.activeId) && model.activeId.value === msg.id;
      const next: CommentThreadsModel = {
        ...model,
        openId: nothing,
        activeId: clearActive ? nothing : model.activeId,
      };
      const cmds: CommentThreadsCmd[] = [
        { kind: "syncOpenCommentFacet", id: nothing },
        { kind: "scheduleLayout" },
      ];
      if (clearActive) {
        cmds.push({ kind: "bridgeActiveCommentId", id: nothing });
      }
      return [next, cmds];
    }
    case "SelectComment": {
      const alreadyOpen = isJust(model.openId) && model.openId.value === msg.id;
      const cmds: CommentThreadsCmd[] = [
        ...openThreadCmds(msg.id, msg.mobileShell, alreadyOpen),
        { kind: "bridgeActiveCommentId", id: just(msg.id) },
        { kind: "emitSelectComment", id: msg.id },
      ];
      if (msg.focusLine) {
        cmds.push({ kind: "scrollToComment", id: msg.id });
      }
      return [
        {
          ...model,
          openId: just(msg.id),
          activeId: just(msg.id),
        },
        cmds,
      ];
    }
    case "ReplyOpened": {
      const alreadyOpen =
        isJust(model.openId) && model.openId.value === msg.threadId;
      return [
        {
          ...model,
          openId: just(msg.threadId),
          activeId: just(msg.threadId),
        },
        [
          ...openThreadCmds(msg.threadId, msg.mobileShell, alreadyOpen),
          { kind: "bridgeActiveCommentId", id: just(msg.threadId) },
        ],
      ];
    }
    case "ClearActiveIf": {
      if (isNothing(model.activeId) || model.activeId.value !== msg.id) {
        return [model, []];
      }
      return [
        { ...model, activeId: nothing },
        [{ kind: "bridgeActiveCommentId", id: nothing }],
      ];
    }
    case "ThreadRemoved": {
      const clearOpen = isJust(model.openId) && model.openId.value === msg.id;
      const clearActive =
        isJust(model.activeId) && model.activeId.value === msg.id;
      if (clearOpen === false && clearActive === false) {
        return [model, [{ kind: "scheduleLayout" }]];
      }
      const cmds: CommentThreadsCmd[] = [{ kind: "scheduleLayout" }];
      if (clearOpen) {
        cmds.unshift({ kind: "syncOpenCommentFacet", id: nothing });
      }
      if (clearActive) {
        cmds.push({ kind: "bridgeActiveCommentId", id: nothing });
      }
      return [
        {
          ...model,
          openId: clearOpen ? nothing : model.openId,
          activeId: clearActive ? nothing : model.activeId,
        },
        cmds,
      ];
    }
    case "CommentTextClick": {
      const alreadyOpen = isJust(model.openId) && model.openId.value === msg.id;
      if (alreadyOpen) return [model, []];
      return [
        {
          ...model,
          openId: just(msg.id),
          activeId: just(msg.id),
        },
        [
          ...openThreadCmds(msg.id, msg.mobileShell, false),
          { kind: "bridgeActiveCommentId", id: just(msg.id) },
          { kind: "flashComment", id: msg.id },
        ],
      ];
    }
    case "SetHovered":
      return [{ ...model, hoveredCommentId: msg.id }, []];
    case "SetCommentsVisible":
      return [{ ...model, commentsVisible: msg.visible }, []];
    case "ScheduleReveal":
      return [model, [{ kind: "scheduleReveal", delayMs: msg.delayMs }]];
    case "RevealComments":
      return [{ ...model, commentsVisible: true }, []];
    default:
      return assertNever(msg);
  }
}

export const updateCommentThreadsFn: UpdateFn<
  CommentThreadsModel,
  CommentThreadsMsg,
  CommentThreadsCmd
> = updateCommentThreads;
